import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import axios from 'axios';
import { format } from 'date-fns';
import { CircleArrowLeft, Mail, Send } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type UnsyncedCustomerEmailItem,
  useSyncUnsyncedEmailToGhl,
  useUnsyncedCustomerEmails,
} from '~entities/payment/api/get-unsynced-customer-emails';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

const PAGE_SIZE = 20;
const columnHelper = createColumnHelper<UnsyncedCustomerEmailItem>();

function getErrorMessage(err: unknown): string | null {
  if (axios.isAxiosError(err) && err.response) {
    const data: unknown = err.response.data as unknown;
    if (data != null && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const msg = obj.message;
      if (typeof msg === 'string' && msg.trim()) return msg;
      if (Array.isArray(msg) && msg.length > 0) {
        const joined = msg
          .map((m) => String(m))
          .filter((s) => s.trim() !== '')
          .join('; ');
        if (joined.trim()) return joined;
      }
      const errStr = obj.error;
      if (typeof errStr === 'string' && errStr.trim()) return errStr;
    }
    if (typeof data === 'string' && data.trim()) return data;
    // Prefer status text only if we could not extract a Nest/validation message above
    if (err.response.status >= 400 && err.response.status < 500) {
      return `Sync failed (${err.response.status}). Check GHL connection permissions or try again.`;
    }
  }
  if (err instanceof Error && err.message) {
    if (!err.message.startsWith('Request failed with status code'))
      return err.message;
    return 'Sync failed. Please try again or check that the contact exists in GHL for this location.';
  }
  return null;
}

function formatTemplateDataDisplay(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

export function PortalToCrmEmailSyncIssuePage() {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useUnsyncedCustomerEmails({
    page,
    limit: PAGE_SIZE,
  });

  const syncToGhl = useSyncUnsyncedEmailToGhl({ page, limit: PAGE_SIZE });
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] =
    useState<UnsyncedCustomerEmailItem | null>(null);

  const handleSendToGhl = useCallback(
    async (id: string) => {
      setSyncingId(id);
      try {
        await syncToGhl.mutateAsync(id);
        toast.success('Email sent to GHL conversation');
      } catch (err: unknown) {
        const message = getErrorMessage(err);
        toast.error(
          message ??
            'Failed to send email to GHL. Contact may not exist for this location.',
        );
      } finally {
        setSyncingId(null);
      }
    },
    [syncToGhl],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('clientName', {
        header: 'Client name',
        cell: (info) => (
          <span className='font-medium'>{info.getValue() ?? '—'}</span>
        ),
      }),
      columnHelper.accessor('clientEmail', { header: 'Client email' }),
      columnHelper.accessor('emailSubject', {
        header: 'Subject',
        cell: (info) => <span className='line-clamp-2'>{info.getValue()}</span>,
      }),
      columnHelper.accessor('emailBody', {
        header: 'Body summary',
        cell: ({ row }) => (
          <button
            type='button'
            onClick={() => {
              setSelectedEmail(row.original);
            }}
            className='line-clamp-2 text-left text-blue-600 underline-offset-2 hover:text-blue-800 hover:underline'
          >
            {row.original.emailBody}
          </button>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created at',
        cell: (info) =>
          info.getValue()
            ? format(new Date(info.getValue()), 'MMM d, yyyy HH:mm')
            : '—',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant='outline'
            size='sm'
            disabled={syncingId !== null}
            onClick={() => {
              void handleSendToGhl(row.original.id);
            }}
          >
            {syncingId === row.original.id ? (
              'Sending…'
            ) : (
              <>
                <Send className='mr-1 size-4' />
                Send to CRM
              </>
            )}
          </Button>
        ),
      }),
    ],
    [handleSendToGhl, syncingId],
  );

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: PAGE_SIZE,
  };

  return (
    <div className='space-y-6'>
      <Link
        to='/dashboard'
        className='flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground'
      >
        <CircleArrowLeft className='size-5' />
        <h2 className='text-xl font-semibold'>
          Portal to CRM Email Sync Issue
        </h2>
      </Link>

      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <Mail className='size-5 text-muted-foreground' />
            <div>
              <CardTitle>Emails not found in CRM</CardTitle>
              <CardDescription>
                Outbound emails that failed to appear in CRM conversations
                (contact not found for this location). Data is scoped to your
                current location only.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className='py-8 text-center text-muted-foreground'>
              Loading…
            </div>
          )}
          {isError && (
            <div className='py-8 text-center text-destructive'>
              Failed to load report. Please try again.
            </div>
          )}
          {!isError && (
            <EnrollioTable
              totalLabel='Unsynced emails'
              filtersLayout='inline'
              filterHeader={(table) => (
                <EnrollioTableSearch
                  table={table}
                  placeholder='Search emails...'
                />
              )}
              data={data?.data ?? []}
              columns={columns}
              rowCount={data?.total ?? 0}
              manualPagination
              pagination={pagination}
              onPaginationChange={(updater) => {
                const next =
                  typeof updater === 'function' ? updater(pagination) : updater;
                setPage(next.pageIndex + 1);
              }}
              getRowId={(row) => row.id}
              isUpdating={isLoading || syncToGhl.isPending}
              emptyContent='No unsynced emails for your location.'
              ariaLabel='Emails not found in CRM'
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedEmail}
        onOpenChange={(open) => {
          if (!open) setSelectedEmail(null);
        }}
      >
        <DialogContent className='max-h-[90vh] max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Email details</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className='space-y-4'>
              <dl className='grid grid-cols-[minmax(0,auto)_1fr] gap-x-4 gap-y-2 text-sm'>
                <dt className='font-medium text-muted-foreground'>
                  Client name
                </dt>
                <dd>{selectedEmail.clientName ?? '—'}</dd>
                <dt className='font-medium text-muted-foreground'>
                  Client email
                </dt>
                <dd className='break-all'>{selectedEmail.clientEmail}</dd>
                <dt className='font-medium text-muted-foreground'>Subject</dt>
                <dd>{selectedEmail.emailSubject || '—'}</dd>
                <dt className='font-medium text-muted-foreground'>
                  Created at
                </dt>
                <dd>
                  {selectedEmail.createdAt
                    ? format(
                        new Date(selectedEmail.createdAt),
                        'MMM d, yyyy HH:mm',
                      )
                    : '—'}
                </dd>
              </dl>
              {selectedEmail.emailBodyHtml && (
                <div>
                  <p className='mb-1 text-sm font-medium text-muted-foreground'>
                    Email as sent (rendered from SendGrid template)
                  </p>
                  <div className='max-h-[50vh] overflow-auto rounded border bg-white'>
                    <iframe
                      title='Email content'
                      sandbox=''
                      srcDoc={selectedEmail.emailBodyHtml}
                      className='h-[400px] w-full border-0'
                    />
                  </div>
                </div>
              )}
              <div>
                <p className='mb-1 text-sm font-medium text-muted-foreground'>
                  Body
                </p>
                <div className='max-h-[40vh] overflow-auto rounded border bg-muted/30 p-3'>
                  <pre className='whitespace-pre-wrap break-words font-sans text-sm'>
                    {selectedEmail.emailBody || '—'}
                  </pre>
                </div>
              </div>
              {selectedEmail.emailTemplateData && (
                <div>
                  <p className='mb-1 text-sm font-medium text-muted-foreground'>
                    Template data sent to SendGrid
                  </p>
                  <p className='mb-2 text-xs text-muted-foreground'>
                    Exact data used to render the email (no HTML stored).
                  </p>
                  <div className='max-h-[40vh] overflow-auto rounded border bg-muted/30 p-3'>
                    <pre className='whitespace-pre-wrap break-words font-mono text-xs'>
                      {formatTemplateDataDisplay(
                        selectedEmail.emailTemplateData,
                      )}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
